import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtInt, fmtDate, todayISO } from "@/lib/format";
import { Plus, Factory, Loader2, Trash2, FlaskConical, Boxes, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Production() {
  const { societes, societeParam, societeId } = useApp();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [nomens, setNomens] = useState([]);
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nOpen, setNOpen] = useState(false);
  const [nForm, setNForm] = useState(null);

  const sid = () => (societeId !== "all" ? societeId : societes[0]?.id || "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, n] = await Promise.all([
        api.get("/production", { params: societeParam }),
        api.get("/production/stats", { params: societeParam }),
        api.get("/production/nomenclatures", { params: societeParam }),
      ]);
      setItems(p.data.items); setStats(s.data); setNomens(n.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [societeId]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const loadProduits = (s) => { if (s) api.get("/produits", { params: { societe_id: s } }).then((r) => setProduits(r.data)); };

  const openNew = () => { const s = sid(); setForm({ societe_id: s, date: todayISO(), atelier: "", produit_id: "", lot: "", quantite_prevue: 0, quantite_produite: 1, poids_unitaire: 0, quantite_conforme: 0, quantite_non_conforme: 0, pertes: 0, responsable: "", equipe: "", heure_debut: "", heure_fin: "", consommer_matieres: true, observations: "" }); loadProduits(s); setOpen(true); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.produit_id) return toast.error("Produit fabriqué requis.");
    if (!form.quantite_produite || Number(form.quantite_produite) <= 0) return toast.error("Quantité produite invalide.");
    setSaving(true);
    try {
      await api.post("/production", { ...form, quantite_prevue: Number(form.quantite_prevue) || 0, quantite_produite: Number(form.quantite_produite), poids_unitaire: Number(form.poids_unitaire) || 0, quantite_conforme: Number(form.quantite_conforme) || 0, quantite_non_conforme: Number(form.quantite_non_conforme) || 0, pertes: Number(form.pertes) || 0 });
      toast.success("Production enregistrée — stock mis à jour."); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const openNomen = () => { const s = sid(); setNForm({ societe_id: s, produit_fini_id: "", composants: [{ produit_id: "", quantite: 1 }] }); loadProduits(s); setNOpen(true); };
  const setNComp = (i, k, v) => setNForm((f) => { const c = [...f.composants]; c[i] = { ...c[i], [k]: v }; return { ...f, composants: c }; });
  const saveNomen = async () => {
    if (!nForm.produit_fini_id) return toast.error("Produit fini requis.");
    if (nForm.composants.some((c) => !c.produit_id)) return toast.error("Chaque composant doit être choisi.");
    try {
      await api.post("/production/nomenclatures", { ...nForm, composants: nForm.composants.map((c) => ({ produit_id: c.produit_id, quantite: Number(c.quantite) })) });
      toast.success("Recette enregistrée."); setNOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const poidsTotal = form ? (Number(form.quantite_produite) || 0) * (Number(form.poids_unitaire) || 0) : 0;

  return (
    <div>
      <PageHeader title="Production journalière" subtitle="Fabrication, lots, pertes et consommation automatique des matières" testid="production-page"
        actions={<Button onClick={openNew} className="gap-2" data-testid="new-prod-btn"><Plus className="h-4 w-4" /> Nouvelle production</Button>} />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6 kpi-grid">
          <KpiCard testid="prod-mois" label="Production du mois" value={fmtInt(stats.prod_mois)} isCurrency={false} icon={Factory} tone="primary" />
          <KpiCard testid="prod-annee" label="Production annuelle" value={fmtInt(stats.prod_annee)} isCurrency={false} icon={Boxes} />
          <KpiCard testid="prod-jour" label="Production du jour" value={fmtInt(stats.prod_jour)} isCurrency={false} icon={Factory} tone="success" />
          <KpiCard testid="prod-pertes" label="Pertes cumulées" value={fmtInt(stats.pertes)} isCurrency={false} icon={AlertTriangle} tone="warning" />
          <KpiCard testid="prod-nc" label="Taux non conforme" value={`${stats.taux_non_conforme}%`} isCurrency={false} icon={AlertTriangle} tone={stats.taux_non_conforme > 5 ? "danger" : "success"} />
        </div>
      )}

      <Tabs defaultValue="prod">
        <TabsList data-testid="production-tabs">
          <TabsTrigger value="prod" className="gap-1.5"><Factory className="h-4 w-4" /> Productions</TabsTrigger>
          <TabsTrigger value="nomen" className="gap-1.5"><FlaskConical className="h-4 w-4" /> Recettes / Nomenclatures</TabsTrigger>
        </TabsList>

        <TabsContent value="prod" className="mt-4">
          <div className="rounded-xl border bg-card overflow-hidden">
            {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              : items.length === 0 ? <EmptyState icon={Factory} title="Aucune production" hint="Enregistrez une production : les matières premières sont consommées et le produit fini entre en stock." />
              : (
                <div className="overflow-x-auto">
                  <Table data-testid="prod-table">
                    <TableHeader><TableRow className="bg-muted/50"><TableHead>N°</TableHead><TableHead>Date</TableHead><TableHead>Produit</TableHead><TableHead>Lot</TableHead><TableHead className="text-right">Produit</TableHead><TableHead className="text-right">Poids total</TableHead><TableHead className="text-right">Conforme</TableHead><TableHead className="text-right">Pertes</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {items.map((p) => (
                        <TableRow key={p.id} data-testid={`prod-row-${p.id}`}>
                          <TableCell className="tabular text-sm font-medium">{p.numero}</TableCell>
                          <TableCell className="tabular text-sm">{fmtDate(p.date)}</TableCell>
                          <TableCell className="text-sm">{p.produit_name}</TableCell>
                          <TableCell className="tabular text-sm">{p.lot || "—"}</TableCell>
                          <TableCell className="tabular text-right">{fmtInt(p.quantite_produite)}</TableCell>
                          <TableCell className="tabular text-right">{fmtInt(p.poids_total)} kg</TableCell>
                          <TableCell className="tabular text-right text-success">{fmtInt(p.quantite_conforme)}</TableCell>
                          <TableCell className="tabular text-right text-destructive">{fmtInt(p.pertes)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
          </div>
        </TabsContent>

        <TabsContent value="nomen" className="mt-4">
          <div className="flex justify-end mb-3"><Button onClick={openNomen} className="gap-2" data-testid="new-nomen-btn"><Plus className="h-4 w-4" /> Nouvelle recette</Button></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nomens.length === 0 && <div className="md:col-span-2 rounded-xl border bg-card"><EmptyState icon={FlaskConical} title="Aucune recette" hint="Définissez la nomenclature (matières premières) de chaque produit fini pour automatiser la consommation." /></div>}
            {nomens.map((n) => (
              <div key={n.id} className="rounded-xl border bg-card p-5">
                <h3 className="font-heading font-bold">{n.produit_fini_name}</h3>
                <p className="text-xs text-muted-foreground mb-3">Pour 1 unité produite :</p>
                <div className="space-y-1.5">
                  {n.composants.map((c, i) => (
                    <div key={i} className="flex justify-between text-sm border-b border-border/50 pb-1"><span>{c.produit_name}</span><span className="tabular font-medium">{fmtInt(c.quantite)}</span></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Production dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="prod-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Nouvelle production</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Produit fabriqué *</Label>
                <Select value={form.produit_id} onValueChange={(v) => setField("produit_id", v)}>
                  <SelectTrigger data-testid="prod-produit"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-popover max-h-64">{produits.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Date</Label><DatePicker value={form.date} onChange={(v) => setField("date", v)} testid="prod-date" /></div>
              <div className="space-y-1.5"><Label>Lot</Label><Input value={form.lot} onChange={(e) => setField("lot", e.target.value)} data-testid="prod-lot" /></div>
              <div className="space-y-1.5"><Label>Atelier</Label><Input value={form.atelier} onChange={(e) => setField("atelier", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Quantité prévue</Label><Input type="number" value={form.quantite_prevue} onChange={(e) => setField("quantite_prevue", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Quantité produite *</Label><Input data-testid="prod-quantite" type="number" value={form.quantite_produite} onChange={(e) => setField("quantite_produite", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Poids unitaire (kg)</Label><Input type="number" value={form.poids_unitaire} onChange={(e) => setField("poids_unitaire", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Poids total</Label><Input value={`${fmtInt(poidsTotal)} kg`} disabled className="tabular" /></div>
              <div className="space-y-1.5"><Label>Conforme</Label><Input type="number" value={form.quantite_conforme} onChange={(e) => setField("quantite_conforme", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Non conforme</Label><Input type="number" value={form.quantite_non_conforme} onChange={(e) => setField("quantite_non_conforme", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Pertes</Label><Input type="number" value={form.pertes} onChange={(e) => setField("pertes", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Responsable</Label><Input value={form.responsable} onChange={(e) => setField("responsable", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Heure début</Label><Input value={form.heure_debut} onChange={(e) => setField("heure_debut", e.target.value)} placeholder="08:00" /></div>
              <div className="space-y-1.5"><Label>Heure fin</Label><Input value={form.heure_fin} onChange={(e) => setField("heure_fin", e.target.value)} placeholder="16:00" /></div>
              <label className="flex items-center gap-2 text-sm sm:col-span-3 mt-1"><input type="checkbox" checked={form.consommer_matieres} onChange={(e) => setField("consommer_matieres", e.target.checked)} data-testid="prod-conso" /> Consommer les matières premières selon la recette (nomenclature)</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} data-testid="prod-submit" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nomenclature dialog */}
      <Dialog open={nOpen} onOpenChange={setNOpen}>
        <DialogContent className="bg-popover max-w-lg" data-testid="nomen-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Recette / Nomenclature</DialogTitle></DialogHeader>
          {nForm && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Produit fini *</Label>
                <Select value={nForm.produit_fini_id} onValueChange={(v) => setNForm((f) => ({ ...f, produit_fini_id: v }))}>
                  <SelectTrigger data-testid="nomen-pf"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-popover max-h-64">{produits.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Label>Matières premières (par unité)</Label>
              {nForm.composants.map((c, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Select value={c.produit_id} onValueChange={(v) => setNComp(i, "produit_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`nomen-comp-${i}`}><SelectValue placeholder="Matière" /></SelectTrigger>
                    <SelectContent className="bg-popover max-h-56">{produits.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input className="w-24 text-right" type="number" value={c.quantite} onChange={(e) => setNComp(i, "quantite", e.target.value)} />
                  <button className="text-muted-foreground hover:text-destructive" onClick={() => setNForm((f) => ({ ...f, composants: f.composants.filter((_, idx) => idx !== i) }))} disabled={nForm.composants.length === 1}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <button onClick={() => setNForm((f) => ({ ...f, composants: [...f.composants, { produit_id: "", quantite: 1 }] }))} className="text-sm text-primary flex items-center gap-1.5" data-testid="nomen-add"><Plus className="h-4 w-4" /> Ajouter une matière</button>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setNOpen(false)}>Annuler</Button><Button onClick={saveNomen} data-testid="nomen-submit">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
